import React, { SetStateAction, useCallback, useEffect, useState } from "react";
import { AHInput, AHSeparator, AHText, Box, ListItem, useTheme } from "arkh";
import * as Location from "expo-location";
import { Duration } from "luxon";
import { useFocusEffect } from "@react-navigation/native";

import { ActivityIndicator, FlatList, View } from "react-native";

import {
  PreloadedQuery,
  fetchQuery,
  graphql,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";
import { WAWeatherSettingsSearchQuery } from "./__generated__/WAWeatherSettingsSearchQuery.graphql";
import { useDebouncedCallback } from "use-debounce";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";

const QUERY = graphql`
  query WAWeatherSettingsSearchQuery($query: String!) {
    searchPlaces(query: $query) {
      id
      displayName
      formattedAddress
    }
  }
`;

const ListItemSearchPrompt = (props: {
  onPress: (placeId: string, desc: string) => void;
}) => {
  const [queryRef, loadQueryF] =
    useQueryLoader<WAWeatherSettingsSearchQuery>(QUERY);

  const env = useRelayEnvironment();

  return (
    <Box style={{ flex: 1 }}>
      <SearchPrompt
        onSearch={(str) => {
          if (str.length === 0) {
            return;
          }
          fetchQuery<WAWeatherSettingsSearchQuery>(env, QUERY, {
            query: str,
          }).subscribe({
            error: () => {},
            complete: () => {
              loadQueryF(
                {
                  query: str,
                },
                {
                  fetchPolicy: "store-only",
                }
              );
            },
          });
        }}
      />
      {queryRef ? (
        <SearchList query={queryRef} onPress={props.onPress} />
      ) : null}
    </Box>
  );
};

function SearchPrompt(props: { onSearch(res: string): void }) {
  const [value, setValue] = useState("");

  const search = useDebouncedCallback(props.onSearch, 500, {
    maxWait: 1000,
  });

  useEffect(() => {
    search(value);
  }, [search, value]);

  return (
    <AHInput
      value={value}
      onChangeText={(v) => {
        setValue(v);
      }}
      autoFocus
      placeholder="Skriv ort eller adress"
      textType="title-small"
      returnKeyType="search"
      defaultStyle
    />
  );
}

function SearchList(props: {
  query: PreloadedQuery<WAWeatherSettingsSearchQuery>;
  onPress: (placeId: string, desc: string) => void;
}) {
  const data = usePreloadedQuery(QUERY, props.query);

  return (
    <Box spaceVertical="small" style={{ flex: 1 }}>
      <FlatList
        style={{ flex: 1 }}
        data={data.searchPlaces}
        ItemSeparatorComponent={AHSeparator}
        renderItem={({ item: place, index }) => {
          return (
            <ListItem
              isFirst={index === 0}
              isLast={data.searchPlaces.length - 1 === index}
              onPress={() => {
                props.onPress(
                  place.id,
                  place.displayName ?? place.formattedAddress ?? ""
                );
              }}
            >
              <ListItem.Title>{place.displayName}</ListItem.Title>
              <ListItem.SubTitle>{place.formattedAddress}</ListItem.SubTitle>
            </ListItem>
          );
        }}
      />
    </Box>
  );
}

type WAWeatherLocation =
  | {
      lat: number;
      lng: number;
      desc: string;
      type: "latlng";
    }
  | {
      placeId: string;
      desc: string;
      type: "place";
    };
async function getReverseGeocode(lat: number, lng: number) {
  return fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyAvm2PaTtjbC-8_YrwkgX2owBIcnw9eAHk`
  )
    .then((r) => r.json())
    .then((r) => r.results[0]?.formatted_address);
}

export const WAWeatherSettings = ({
  location,
  setLocation,
}: {
  location: WAWeatherLocation | null;
  setLocation(_: SetStateAction<WAWeatherLocation | null>): void;
}) => {
  const [status, setStatus] = useState<
    "loading" | "idle" | "done" | "error" | "gps-loading"
  >("idle");

  const theme = useTheme();
  const intl_tokens = useIntlTokens();
  useFocusEffect(
    useCallback(() => {
      (async () => {
        try {
          setStatus("gps-loading");
          const res = await Location.requestForegroundPermissionsAsync();
          if (res.status !== "granted") {
            setStatus("error");
            return;
          }

          const location =
            (await Location.getLastKnownPositionAsync({
              maxAge: Duration.fromObject({ minutes: 10 }).as("milliseconds"),
            })) ??
            (await Location.getCurrentPositionAsync({
              accuracy: Location.LocationAccuracy.Low,
            }));

          setLocation({
            lat: location.coords.latitude,
            lng: location.coords.longitude,
            desc: await getReverseGeocode(
              location.coords.latitude,
              location.coords.longitude
            ),
            type: "latlng",
          });
          setStatus("done");
        } catch {
          setStatus("error");
        }
      })();
    }, [setLocation])
  );

  if (status === "gps-loading") {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator color={theme.washedText} size="small" />
        <AHText spaceTop="medium">Hittar din plats</AHText>
      </View>
    );
  }

  return (
    <Box spaceHorizontal="medium" spaceVertical="medium" style={{ flex: 1 }}>
      {location ? (
        <ListItem
          isFree
          border="border-primary"
          onPress={() => {
            setLocation(null);
          }}
          right={
            <ListItem.DetailActionText>
              {intl_tokens.Edit}
            </ListItem.DetailActionText>
          }
        >
          <ListItem.Title>{location.desc}</ListItem.Title>
        </ListItem>
      ) : (
        <ListItemSearchPrompt
          onPress={(placeId, desc) => {
            setLocation({
              type: "place",
              placeId,
              desc,
            });
          }}
        />
      )}
    </Box>
  );
};
